'use client'

import {
  formatDisplayTime,
  TimePickerInput,
  TimePickerProvider,
  TimePickerTypeSelector,
  type TimePickerValue,
} from '@repo/features/time-picker'
import { z } from '@repo/schema'
import { GetTimeSchema } from '@repo/schema/time'
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel } from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { useStore } from '@tanstack/react-form'
import { ComponentProps, ReactNode } from 'react'

import { ReadOnlyField, useReadOnly } from '../components/readonly.tsx'
import { useFieldContext } from '../lib/form-context.ts'

type FormTimeInputProps = {
  description?: ReactNode | string
  disabled?: boolean
  isPending?: boolean
  label?: ReactNode | string
  required?: boolean
  size?: 'default' | 'large'
}

type TimeValue = z.infer<typeof GetTimeSchema>

export const FormTimeInput = ({
  description,
  disabled = false,
  isPending = false,
  label,
  mode = 'schema',
  required,
  size = 'default',
}: FormTimeInputProps & Pick<ComponentProps<typeof TimePickerProvider>, 'mode'>) => {
  const field = useFieldContext<TimeValue>()
  const errors = field.state.meta.errors ?? []
  const isInvalid = errors.length > 0
  const value = useStore(field.store, store => store.value)
  const isReadOnly = useReadOnly()
  const fieldLabel =
    label && typeof label === 'string' ? (
      <FieldLabel className={cn(required && 'required gap-0.5')} htmlFor={field.name}>
        {label}
      </FieldLabel>
    ) : (
      label
    )

  const currentValue: TimePickerValue = {
    from: value?.from,
    to: value?.to,
    type: value?.type ?? 'between',
  }

  const handleChange = (nextValue: TimePickerValue) => {
    field.handleChange({
      from: nextValue.from,
      to: nextValue.to,
      type: nextValue.type,
    })
  }

  if (isReadOnly) {
    return (
      <ReadOnlyField label={fieldLabel}>
        {value ? formatDisplayTime(currentValue) : '-'}
      </ReadOnlyField>
    )
  }

  return (
    <Field className="gap-1" data-invalid={isInvalid} data-sentry-element="Field" data-sentry-component="FormTimeInput" data-sentry-source-file="form-time-input.tsx">
      <FieldContent data-sentry-element="FieldContent" data-sentry-source-file="form-time-input.tsx">
        {fieldLabel}
        <TimePickerProvider
          disabled={disabled || isPending}
          invalid={isInvalid}
          mode={mode}
          onChange={handleChange}
          size={size}
          value={currentValue}
        data-sentry-element="TimePickerProvider" data-sentry-source-file="form-time-input.tsx">
          <div className="flex gap-2" onBlurCapture={field.handleBlur}>
            <TimePickerTypeSelector className="w-fit" data-sentry-element="TimePickerTypeSelector" data-sentry-source-file="form-time-input.tsx" />
            <div className="flex w-fit items-center gap-1" id={field.name}>
              <TimePickerInput data-sentry-element="TimePickerInput" data-sentry-source-file="form-time-input.tsx" />
            </div>
          </div>
        </TimePickerProvider>
        {description && typeof description === 'string' ? (
          <FieldDescription>{description}</FieldDescription>
        ) : (
          description
        )}
        <FieldError className={'text-sm'} errors={errors} data-sentry-element="FieldError" data-sentry-source-file="form-time-input.tsx" />
      </FieldContent>
    </Field>
  )
}
